import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileWarning, Loader2, Send } from 'lucide-react';
import { fetchJson, formatMoney } from '@/lib/api';

// The same claim types the review screen understands (insurance.VALID_CLAIM_TYPES).
// This page used to file into a collection nobody reviewed; it now goes through
// the same endpoint and rules as the SelleTrans claim form.
const CLAIM_TYPES = [
  ['shipping_loss', 'Lost in transit'],
  ['shipping_damage', 'Damaged in transit'],
  ['porch_piracy', 'Stolen after delivery'],
  ['shortage_of_goods', 'Shortage'],
  ['chargeback', 'Chargeback'],
  ['return', 'Return'],
  ['customs', 'Customs'],
];
const LABEL = Object.fromEntries(CLAIM_TYPES);

const VACIO = {
  verification_number: '',
  claim_type: 'shipping_loss',
  incident_date: new Date().toISOString().slice(0, 10),
  discovered_date: '',
  amount_claimed: '',
  description: '',
};

export default function ClaimsPage() {
  const { token } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(VACIO);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const d = await fetchJson('/insurance/selletrans/claims', { token });
      setLoadError('');
      setClaims(d.records || []);
    } catch (e) {
      setLoadError(e.message || 'Could not load your claims.');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        ...form,
        amount_claimed: parseFloat(form.amount_claimed),
        discovered_date: form.discovered_date || null,
      };
      const d = await fetchJson('/insurance/selletrans/claims', { token, method: 'POST', body });
      toast.success(`Claim ${d.claim_number} submitted`);
      setForm(VACIO);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="claims-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <FileWarning className="w-6 h-6 text-red-500" /> Open a Claim
        </h1>
        <p className="text-slate-500 mt-1">File against a Parcel Risk coverage number (SLT-…). Tell us within 48 hours of finding the problem; send the paperwork within 14 days.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">New Claim</CardTitle><CardDescription>The claim goes to review. You can follow it below and on the Parcel Risk page.</CardDescription></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="claim-vn">Coverage # (SLT-…)</Label>
              <Input id="claim-vn" required value={form.verification_number} onChange={e => setForm(f => ({ ...f, verification_number: e.target.value }))} placeholder="SLT-20260830-…" data-testid="verify-input" />
            </div>
            <div>
              <Label>What happened</Label>
              <Select value={form.claim_type} onValueChange={v => setForm(f => ({ ...f, claim_type: v }))}>
                <SelectTrigger data-testid="claim-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CLAIM_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="claim-incident">Incident date</Label>
              <Input id="claim-incident" required type="date" value={form.incident_date} onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="claim-discovered">Found out on (if later)</Label>
              <Input id="claim-discovered" type="date" value={form.discovered_date} onChange={e => setForm(f => ({ ...f, discovered_date: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="claim-amount">Claim amount ($)</Label>
              <Input id="claim-amount" required type="number" step="0.01" min="0.01" value={form.amount_claimed} onChange={e => setForm(f => ({ ...f, amount_claimed: e.target.value }))} data-testid="amount-input" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="claim-description">Description</Label>
              <Textarea id="claim-description" required rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened, when, how it was discovered, tracking number." data-testid="description-input" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={submitting} data-testid="submit-claim-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Claim
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">My Claims</CardTitle></CardHeader>
        <CardContent>
          {loadError ? (
            <p className="text-sm text-red-600 py-4 text-center" data-testid="claims-error">{loadError}</p>
          ) : claims.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No claims yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b"><th className="text-left py-2">Claim</th><th>Coverage</th><th>Type</th><th className="text-right">Amount</th><th>Status</th><th>Filed</th></tr></thead>
              <tbody>
                {claims.map(c => (
                  <tr key={c.id} className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-xs">{c.claim_number}</td>
                    <td className="font-mono text-xs">{c.verification_number}</td>
                    <td>{LABEL[c.claim_type] || c.claim_type}</td>
                    <td className="text-right tabular-nums">{formatMoney(c.amount_claimed)}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">{String(c.status || '').replace(/_/g, ' ')}</span></td>
                    <td className="text-slate-500 text-xs">{c.filed_at ? new Date(c.filed_at).toLocaleDateString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
